import React from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown } from 'lucide-react';

interface DropdownProps {
  title: string | React.ReactNode;
  to?: string;
  children: React.ReactNode;
  isActive?: boolean;
}

export function Dropdown({ title, to, children, isActive }: DropdownProps) {
  const [isOpen, setIsOpen] = React.useState(false);
  const dropdownRef = React.useRef<HTMLDivElement>(null);
  const buttonRef = React.useRef<HTMLAnchorElement>(null);

  React.useEffect(() => {
    if (isOpen && dropdownRef.current) {
      // Use requestAnimationFrame to batch layout reads/writes and prevent forced reflows
      requestAnimationFrame(() => {
        if (!dropdownRef.current) return;
        
        const rect = dropdownRef.current.getBoundingClientRect();
        const viewportWidth = window.innerWidth;
        
        // Batch DOM writes after reads to prevent forced reflows
        requestAnimationFrame(() => {
          if (!dropdownRef.current) return;
          
          if (rect.right > viewportWidth) {
            dropdownRef.current.style.left = 'auto';
            dropdownRef.current.style.right = '0';
          }
        });
      });
    }
  }, [isOpen]);

  React.useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        setIsOpen(false);
        buttonRef.current?.focus();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen]);
  return (
    <div
      className="group relative z-[150]"
      onMouseEnter={() => setIsOpen(true)}
      onMouseLeave={() => setIsOpen(false)}
    >
      <Link
        ref={buttonRef}
        to={to || '#'}
        className={`menu-item px-3 py-3 flex items-center gap-2 nav-link text-sm font-medium transition-all duration-200 hover:text-accessible-red-600 ${isActive ? 'text-accessible-red-600' : 'text-accessible-gray-700 dark:text-accessible-gray-300 high-contrast:text-white'}`}
        aria-expanded={isOpen}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setIsOpen(!isOpen);
          }
        }}
      >
        <span>{title}</span>
        <ChevronDown className={`w-3 h-3 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} />
      </Link>
      <div
        ref={dropdownRef}
        className={`absolute left-0 top-full min-w-[200px] bg-white/95 dark:bg-accessible-gray-900/95 high-contrast:bg-black backdrop-blur-md shadow-xl rounded-lg border border-accessible-gray-200/50 dark:border-accessible-gray-700/50 high-contrast:border-white/20 transform transition-all duration-200 origin-top mt-1 z-[160] ${isOpen ? 'scale-y-100 opacity-100' : 'scale-y-95 opacity-0 invisible'}`}
        role="menu"
        aria-orientation="vertical"
      >
        {children}
      </div>
    </div>
  );
}